import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ArrowRight, Lightbulb, Target, TrendingUp, Users, FileText, MessageSquare, BarChart3, CheckCircle2, AlertCircle, Zap, Rocket, Globe, Brain } from "lucide-react";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ScoreGauge } from "@/components/ScoreGauge";
import { LevelBadge } from "@/components/LevelBadge";
import { LeadForm, LeadFormData } from "@/components/LeadForm";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import { useWebsiteScan } from "@/hooks/use-website-scan";
import { DownloadReportEmailForm } from "@/components/DownloadReportEmailForm";
import { ReportFooter } from "@/components/ReportFooter";
import { QuizAILoader } from "@/components/QuizAILoader";
import { AIInsightCard } from "@/components/AIInsightCard";
import { Skeleton } from "@/components/ui/skeleton";
import { useReportTracking } from "@/hooks/use-report-tracking";
const ReportLow = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState<any>(null);
  const [isLoadingAI, setIsLoadingAI] = useState(true);
  const {
    score,
    answers
  } = location.state || {};
  const band = 'low';
  const reportToken = (location.state as any)?.reportToken;
  const quizSubmissionId = (location.state as any)?.quizSubmissionId;
  const leadSubmissionId = (location.state as any)?.leadSubmissionId;
  
  // Track report view
  useReportTracking({
    reportToken,
    quizSubmissionId,
    leadSubmissionId
  });
  
  const {
    logoUrl
  } = useWebsiteScan({
    initialScan: (location.state as any)?.websiteScan,
    domain: answers?.[4] as string || undefined,
    quizSubmissionId: quizSubmissionId || undefined
  });

  // Helper function to format domain for display
  const formatDomainForDisplay = (domain: string): string => {
    if (!domain) return '';
    return domain.replace(/^https?:\/\//, '').replace(/^www\./, '').trim();
  };

  // Track report view
  useEffect(() => {
    if (typeof window !== 'undefined' && window.fbq) {
      // Custom event
      window.fbq('trackCustom', 'ReportViewed', {
        report_type: band,
        score: score,
        timestamp: new Date().toISOString()
      });

      // Standard ViewContent event
      window.fbq('track', 'ViewContent', {
        content_name: 'LinkedIn Readiness Report - Low',
        content_category: 'report',
        content_type: band,
        value: score
      });
    }
  }, [band, score]);

  // Load AI analysis from database (already generated during quiz completion)
  useEffect(() => {
    const loadAnalysis = async () => {
      try {
        setIsLoadingAI(true);
        const quizSubmissionId = (location.state as any)?.quizSubmissionId;
        
        if (quizSubmissionId) {
          logger.debug('Loading AI analysis from database for quiz:', quizSubmissionId);
          
          // Try to fetch from database first
          const { data: quizData, error: dbError } = await supabase
            .from('quiz_submissions')
            .select('ai_analysis')
            .eq('id', quizSubmissionId)
            .single();
          
          if (dbError) {
            console.error('Error loading AI analysis from database:', dbError);
          } else if (quizData?.ai_analysis) {
            logger.debug('AI analysis loaded from database');
            setAiAnalysis(quizData.ai_analysis);
            setIsLoadingAI(false);
            return;
          }
        }
        
        // Fallback: Generate if not in database (legacy quizzes)
        logger.debug('No AI analysis in database, generating as fallback');
        const quizData = {
          answers: {
            ...(answers || {}),
            found_linkedin_profile: (location.state as any)?.foundLinkedInProfile
          },
          websiteScan: (location.state as any)?.websiteScan || {},
          competitorAnalysis: (location.state as any)?.competitorAnalysis || {},
          interstitialAnswers: (location.state as any)?.interstitialAnswers || {}
        };

        const { data, error } = await supabase.functions.invoke('generate-quiz-recommendations', {
          body: { quizData, band: 'low', quizSubmissionId }
        });

        if (error) {
          console.error('Error generating AI analysis:', error);
          setAiAnalysis(null);
        } else if (data?.analysis) {
          setAiAnalysis(data.analysis);
          console.log('✅ AI analysis generated and saved by edge function');
        }
      } catch (error) {
        console.error('Failed to load AI analysis:', error);
        setAiAnalysis(null);
      } finally {
        setIsLoadingAI(false);
      }
    };
    if (answers && Object.keys(answers).length > 0) {
      loadAnalysis();
    } else {
      setIsLoadingAI(false);
    }
  }, [answers, band, location.state]);
  const handleSubmit = async (data: LeadFormData) => {
    setIsSubmitting(true);
    try {
      logger.debug('Submitting lead via edge function');
      const quizSubmissionId = (location.state as any)?.quizSubmissionId || null;
      const {
        data: result,
        error
      } = await supabase.functions.invoke('submit-lead', {
        body: {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone,
          company: null,
          position: null,
          quizScore: score,
          quizBand: band,
          quizAnswers: answers,
          quizSubmissionId: quizSubmissionId
        }
      });
      if (error) {
        logger.error('Submission error', error);
        throw error;
      }
      if (!result.success) {
        throw new Error(result.error || 'Submission failed');
      }
      logger.info('Lead submission successful');

      // Track headline conversion with lead_submission_id
      const sessionId = localStorage.getItem('headline_session_id');
      const variantId = localStorage.getItem('assigned_variant_id');
      if (sessionId && variantId && result.leadId) {
        try {
          await supabase.from('headline_conversions').insert({
            variant_id: variantId,
            session_id: sessionId,
            lead_submission_id: result.leadId,
            quiz_submission_id: quizSubmissionId || null
          });
          logger.info('✅ Lead conversion tracked for A/B test');
        } catch (convError) {
          logger.error('❌ Failed to track conversion', convError);
        }
      }

      // Track lead form submission
      if (typeof window !== 'undefined' && window.fbq) {
        window.fbq('trackCustom', 'LeadFormSubmitted', {
          report_band: band,
          score: score,
          form_type: 'Report Lead Form'
        });
        
        // Also track as standard Lead event
        window.fbq('track', 'Lead', {
          content_name: 'LinkedIn Readiness Report',
          content_category: band,
          value: score
        });
      }
      
      // Push lead form submission to GTM dataLayer
      if (typeof window !== 'undefined' && (window as any).dataLayer) {
        (window as any).dataLayer.push({
          event: 'lead_form_submitted',
          lead_id: result.leadId,
          report_band: band,
          quiz_score: score,
          email: data.email,
          event_category: 'lead_generation',
          event_label: `Band: ${band} | Score: ${score}`
        });
        console.log('✅ GTM lead form submission event pushed');
      }
      toast.success("Dziękujemy! Twój raport został zapisany.");
      setIsSubmitting(false);
      setIsDialogOpen(false);
      setTimeout(() => {
        navigate("/dziekujemy");
      }, 500);
    } catch (error) {
      logger.error('Error saving lead', error);
      toast.error("Wystąpił błąd. Spróbuj ponownie.");
      setIsSubmitting(false);
    }
  };
  const quickWins = [{
    id: "1",
    label: "Profil firmowy z CTA i opisem oferty"
  }, {
    id: "2",
    label: "ICP lista 100+ firm z decydentami"
  }, {
    id: "3",
    label: "Pierwszy post ekspercki (case study)"
  }, {
    id: "4",
    label: "Profile osobiste zespołu - optymalizacja"
  }, {
    id: "5",
    label: "Konfiguracja Sales Navigator"
  }];
  const recommendations = [{
    icon: <Target className="h-10 w-10 text-primary" />,
    title: "Utwórz lub uzupełnij stronę firmową",
    description: "Dodaj opis, CTA, ofertę i połącz z profilami pracowników. To Twoja wizytówka na LinkedIn.",
    howTo: ["Uzupełnij sekcję 'O nas' z jasną propozycją wartości", "Dodaj CTA (Call-to-Action) prowadzące do landing page", "Wypełnij wszystkie sekcje: produkty, usługi, specjalizacje", "Połącz profile pracowników ze stroną firmową"]
  }, {
    icon: <Users className="h-10 w-10 text-primary" />,
    title: "Zdefiniuj ICP i stwórz listę kont docelowych",
    description: "Określ idealnego klienta i przygotuj listę 100-500 potencjalnych firm z kluczowymi decydentami.",
    howTo: ["Stwórz profil idealnego klienta (branża, wielkość, rola)", "Użyj Sales Navigator do znalezienia firm spełniających kryteria", "Zidentyfikuj 2-3 decydentów w każdej firmie docelowej", "Stwórz listy w Sales Navigator dla systematycznego działania"]
  }, {
    icon: <FileText className="h-10 w-10 text-primary" />,
    title: "Opublikuj pierwszy post ekspercki",
    description: "Podziel się case study lub wiedzą branżową. Pokaż, że rozumiesz problemy klientów.",
    howTo: ["Wybierz jeden sukces klienta lub rozwiązany problem", "Struktura: Problem → Rozwiązanie → Rezultat", "Dodaj dane liczbowe pokazujące efekt (ROI, oszczędności)", "Zakończ pytaniem zachęcającym do dyskusji"]
  }, {
    icon: <MessageSquare className="h-10 w-10 text-primary" />,
    title: "Zoptymalizuj profile osobiste zespołu",
    description: "Profile handlowców i specjalistów powinny jasno komunikować wartość i ekspertyzę.",
    howTo: ["Nagłówek: rola + wartość dla klienta (nie tylko stanowisko)", "Sekcja O mnie: problemy które rozwiązujesz, dla kogo", "Doświadczenie: dodaj osiągnięcia i rezultaty biznesowe", "Spersonalizowany URL i profesjonalne zdjęcie"]
  }, {
    icon: <TrendingUp className="h-10 w-10 text-primary" />,
    title: "LinkedIn Outreach - pierwsze kroki w nawiązywaniu kontaktu",
    description: "Rozpocznij systematyczny outreach do potencjalnych klientów na LinkedIn. Naucz się jak nawiązywać wartościowe relacje bez spamowania.",
    howTo: ["Zacznij od connection request z personalizowaną notką (max 300 znaków)", "Pierwsze wiadomości: pytaj o problemy, nie sprzedawaj od razu", "Stwórz template z 3-4 wariantami pierwszej wiadomości", "Follow-up po 3-7 dniach jeśli brak odpowiedzi (bez presji)", "Systematyka: 10-15 nowych kontaktów tygodniowo na start", "Trackuj odpowiedzi i uczenie się: co działa, co nie"]
  }];
  const stats = [{
    label: "Czas wdrożenia",
    value: "Szybki",
    progress: 25
  }, {
    label: "Wymagany budżet",
    value: "Niski",
    progress: 30
  }, {
    label: "Priorytet",
    value: "Krytyczny",
    progress: 100
  }];
  const commonMistakes = [{
    icon: <AlertCircle className="h-6 w-6 text-destructive" />,
    mistake: "Brak regularności w publikacjach",
    solution: "Zaplanuj conajmniej 1 post tygodniowo na najbliższe 3 miesiące"
  }, {
    icon: <AlertCircle className="h-6 w-6 text-destructive" />,
    mistake: "Zbyt promocyjne treści",
    solution: "Zastosuj zasadę 80/20: 80% wartości edukacyjnej, 20% promocji"
  }, {
    icon: <AlertCircle className="h-6 w-6 text-destructive" />,
    mistake: "Brak połączenia z CRM",
    solution: "Od początku śledź źródło leadów i mierz efekty"
  }];
  return <>
    <Helmet>
      <meta name="robots" content="noindex, nofollow" />
    </Helmet>
    <ScrollArea className="h-screen w-full">
      <div className="min-h-screen gradient-subtle py-6 md:py-12">
        <div className="container px-3 sm:px-4">
          <div className="max-w-5xl mx-auto space-y-6 md:space-y-8 pb-12">
            {/* Domain header - always visible */}
            <Card className="gradient-hero border-none shadow-xl mb-6">
              <CardContent className="p-6 md:p-8 text-center">
                <div className="flex flex-col items-center gap-4">
                  {logoUrl ? <div className="rounded-lg bg-white p-3 shadow-lg">
                      <img src={logoUrl} alt="Company logo" className="h-12 w-auto max-w-[200px] object-contain" onError={e => {
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.nextElementSibling?.classList.remove('hidden');
                  }} />
                      <div className="hidden rounded-full bg-white/20 p-3">
                        <Globe className="h-8 w-8 text-white" />
                      </div>
                    </div> : <div className="rounded-full bg-white/20 p-3">
                      <Globe className="h-8 w-8 text-white" />
                    </div>}
                  <div>
                    {answers?.[4] && (answers[4] as string).trim() !== '' ? <>
                        <p className="text-white/90 text-lg mb-2">
                          Osiągnięty wynik dla
                        </p>
                        <h2 className="text-3xl md:text-4xl font-bold text-white">
                          {formatDomainForDisplay(answers[4] as string)}
                        </h2>
                      </> : <h2 className="text-3xl md:text-4xl font-bold text-white">
                        Twój osiągnięty wynik
                      </h2>}
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Score Section - only show if score exists */}
            {score && <Card className="gradient-card border-none shadow-2xl overflow-hidden">
                <CardContent className="p-0">
                  {/* Top section with gradient background */}
                  <div className="bg-gradient-to-br from-blue-500/5 via-blue-500/10 to-blue-500/5 px-6 pt-12 pb-8">
                    <div className="text-center mb-8">
                      <h2 className="text-4xl md:text-5xl font-bold mb-3 bg-gradient-to-r from-blue-600 via-blue-500 to-blue-600 bg-clip-text text-transparent">
                        Sleeper
                      </h2>
                      <p className="text-lg text-muted-foreground">
                        Poziom gotowości LinkedIn
                      </p>
                    </div>
                    
                    {/* Score and Badge in elegant layout */}
                    <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16">
                      <div className="transform hover:scale-105 transition-transform duration-300">
                        <ScoreGauge score={score} />
                      </div>
                      <div className="transform hover:scale-105 transition-transform duration-300">
                        <LevelBadge level="low" />
                      </div>
                    </div>
                  </div>
                  
                  {/* Bottom section with AI-generated description */}
                  <div className="px-6 py-8 bg-card">
                    <div className="max-w-3xl mx-auto">
                      {isLoadingAI ? <QuizAILoader /> : aiAnalysis ? aiAnalysis.areas ? <div className="space-y-4">
                            {aiAnalysis.areas.map((area: any, idx: number) => <AIInsightCard key={idx} title={area.name} content={area.content} icon={<Rocket className="h-5 w-5 text-primary" />} variant="highlight" />)}
                            {aiAnalysis.keyInsights && aiAnalysis.keyInsights.length > 0 && <AIInsightCard title="Kluczowe obserwacje" content={aiAnalysis.keyInsights} icon={<Brain className="h-5 w-5 text-primary" />} variant="default" />}
                          </div> : <AIInsightCard title={aiAnalysis.title || "Co oznacza Twój wynik"} content={aiAnalysis.description} icon={<Rocket className="h-5 w-5 text-primary" />} variant="highlight" /> : <AIInsightCard title="Co oznacza Twój wynik" content={["Twoja firma stoi u progu transformacji w LinkedIn Social Selling. Obecny wynik pokazuje, że macie ogromny potencjał do wykorzystania - LinkedIn to dziś najpotężniejszy kanał B2B, gdzie każdy punkt bazowy oznacza realne możliwości biznesowe.", "Z odpowiednią strategią i konsekwentnym działaniem przez najbliższe 3 miesiące, możecie zbudować solidną pozycję, która zacznie generować wartościowe leady bez konieczności dużych inwestycji reklamowych."]} icon={<Brain className="h-5 w-5 text-primary" />} variant="default" />}
                    </div>
                  </div>
                </CardContent>
              </Card>}
            
            
            {/* CTA Button to open form modal */}
            <Card className="gradient-cta-red border-none shadow-xl overflow-hidden">
              <CardHeader className="text-center space-y-6 pb-8">
                <div className="flex justify-center">
                  <div className="inline-flex items-center gap-2 bg-white/20 text-white px-4 py-2 rounded-full text-sm font-medium backdrop-blur-sm">
                    <Zap className="h-4 w-4" />
                    Zacznij działać i zbuduj nowy skuteczny kanał sprzedaży
                  </div>
                </div>
                
                <CardTitle className="text-2xl md:text-4xl text-white leading-tight px-4">
                  Dowiedz się, jak LinkedIn buduje kontakty, sprzedaż i reputację lidera.
                </CardTitle>
                
                <div className="max-w-2xl mx-auto space-y-4 px-4">
                  <h3 className="text-xl md:text-2xl font-semibold text-white/95">
                    Jesteś na starcie z LinkedIn. Zobacz, czy i jak w ogóle warto zacząć.
                  </h3>
                  
                  <p className="text-base text-white/90 leading-relaxed">
                    Twój wynik pokazuje, że brakuje fundamentów. Pokażemy Ci, czy LinkedIn ma sens w Twojej sytuacji i jak w kilka tygodni uporządkować obecność, żeby zacząć prowadzić pierwsze rozmowy z idealnymi klientami.
                  </p>
                </div>

                {/* Benefits list */}
                <div className="max-w-2xl mx-auto pt-4 space-y-3 text-left px-4">
                  <div className="flex items-start gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-4 hover:bg-white/15 transition-colors">
                    <CheckCircle2 className="h-6 w-6 text-white shrink-0 mt-0.5" />
                    <p className="text-white/95 font-medium">Ocena, czy LinkedIn ma sens dla Twojej firmy na tym etapie</p>
                  </div>
                  
                  <div className="flex items-start gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-4 hover:bg-white/15 transition-colors">
                    <CheckCircle2 className="h-6 w-6 text-white shrink-0 mt-0.5" />
                    <p className="text-white/95 font-medium">Rekomendowany sposób bezpiecznego startu bez przepalania budżetu</p>
                  </div>
                  
                  <div className="flex items-start gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-4 hover:bg-white/15 transition-colors">
                    <CheckCircle2 className="h-6 w-6 text-white shrink-0 mt-0.5" />
                    <p className="text-white/95 font-medium">Orientacyjny rząd inwestycji potrzebny na początek</p>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="pt-0 pb-8 px-4 space-y-6 my-0">
                <div className="max-w-2xl mx-auto space-y-4">
                  <Button size="lg" variant="secondary" className="w-full flex items-center justify-center gap-2 text-lg py-6 h-auto bg-white text-[hsl(var(--cta-red))] hover:bg-white/90 hover:scale-105 transition-all shadow-xl" onClick={() => {
                  if (typeof window !== 'undefined' && window.fbq) {
                    window.fbq('trackCustom', 'CTAClicked', {
                      cta_type: 'Safe Start Consultation',
                      report_band: band,
                      button_text: 'Porozmawiajmy o bezpiecznym starcie'
                    });
                  }
                  document.getElementById('lead-form-trigger')?.click();
                }}>
                    Porozmawiajmy o bezpiecznym starcie
                    <ArrowRight className="h-5 w-5" />
                  </Button>
                  
                  <p className="text-sm text-white/80 text-center max-w-xl mx-auto leading-relaxed">
                    Rozmowa wsparta Twoim wynikiem i 8+ latami doświadczenia w LinkedIn. Konkretne rekomendacje dla Twojej sytuacji.
                  </p>
                </div>

                {/* Social proof section */}
                <div className="max-w-2xl mx-auto pt-6 border-t border-white/20">
                  <div className="flex items-center justify-center gap-3 text-white/90">
                    <div className="flex -space-x-2">
                      <div className="w-8 h-8 rounded-full bg-white/20 border-2 border-white flex items-center justify-center">
                        <Users className="h-4 w-4 text-white" />
                      </div>
                      <div className="w-8 h-8 rounded-full bg-white/20 border-2 border-white flex items-center justify-center">
                        <TrendingUp className="h-4 w-4 text-white" />
                      </div>
                      <div className="w-8 h-8 rounded-full bg-white/20 border-2 border-white flex items-center justify-center">
                        <Target className="h-4 w-4 text-white" />
                      </div>
                    </div>
                    <p className="text-sm font-medium">
                      Ponad 50 firm B2B rozpoczęło swój LinkedIn journey z naszym wsparciem
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Header */}
            <div className="text-center mb-12 md:mb-16 mt-28 md:mt-40 animate-fade-in">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 md:mb-6 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent mx-0 my-0 px-0 lg:text-6xl">
                Twoje indywidualne zalecenia: Pierwsze dni z LinkedIn
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto px-4">
                Pierwsze kroki już za Tobą, ale to wciąż za mało. Masz ogromną przestrzeń, żeby zacząć efektywnie generować sprzedaż przez LinkedIn.
              </p>
            </div>

            {/* Stats Overview */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-6 mb-6 md:mb-8">
              {stats.map((stat, index) => <Card key={index} className="border-none shadow-lg">
                  <CardContent className="p-4 md:p-6">
                    <div className="text-xs md:text-sm text-muted-foreground mb-1 md:mb-2">{stat.label}</div>
                    <div className="text-xl md:text-2xl font-bold mb-2 md:mb-3">{stat.value}</div>
                    <Progress value={stat.progress} className="h-2" />
                  </CardContent>
                </Card>)}
            </div>
            
            {/* Main Recommendations */}
            <Card className="gradient-card border-none shadow-xl">
              <CardHeader className="p-4 md:p-6">
                <CardTitle className="text-xl sm:text-2xl md:text-3xl flex items-center gap-2 md:gap-3">
                  <BarChart3 className="h-6 w-6 md:h-8 md:w-8 text-primary flex-shrink-0" />
                  <span>Twoje zalecenia krok po kroku</span>
                </CardTitle>
                <CardDescription className="text-sm md:text-base">
                  Fundamenty Social Sellingu - działania które dadzą Ci solidną podstawę
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 md:space-y-8 p-4 md:p-6">
                {recommendations.map((rec, index) => <div key={index} className="group">
                    <div className="flex flex-col sm:flex-row gap-4 md:gap-6 p-4 md:p-6 rounded-xl bg-gradient-to-br from-secondary/50 to-secondary/30 hover:shadow-lg transition-all">
                      <div className="flex-shrink-0">{rec.icon}</div>
                      <div className="flex-1">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
                          <h3 className="font-bold text-lg md:text-xl">{rec.title}</h3>
                          <span className="text-xs md:text-sm font-semibold text-primary bg-primary/10 px-2 md:px-3 py-1 rounded-full whitespace-nowrap self-start">
                            Krok {index + 1}
                          </span>
                        </div>
                        <p className="text-muted-foreground mb-3 md:mb-4 text-sm md:text-base">{rec.description}</p>
                        <div className="bg-background/50 rounded-lg p-3 md:p-4 space-y-2">
                          <div className="font-semibold text-xs md:text-sm mb-2 md:mb-3 flex items-center gap-2">
                            <CheckCircle2 className="h-3 w-3 md:h-4 md:w-4 text-primary flex-shrink-0" />
                            Jak to zrobić:
                          </div>
                          <ul className="space-y-1.5 md:space-y-2">
                            {rec.howTo.map((step, i) => <li key={i} className="flex gap-2 md:gap-3 text-xs md:text-sm">
                                <span className="text-primary font-bold flex-shrink-0">{i + 1}.</span>
                                <span className="text-muted-foreground">{step}</span>
                              </li>)}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>)}
              </CardContent>
            </Card>

            {/* Common Mistakes */}
            <Card className="border-none shadow-xl bg-gradient-to-br from-destructive/5 to-background">
              <CardHeader className="p-4 md:p-6">
                <CardTitle className="text-xl md:text-2xl flex items-center gap-2 md:gap-3">
                  <AlertCircle className="h-5 w-5 md:h-7 md:w-7 text-destructive flex-shrink-0" />
                  <span>Najczęstsze błędy na starcie</span>
                </CardTitle>
                <CardDescription className="text-sm md:text-base">
                  Uniknij tych pułapek, by nie zmarnować czasu
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 md:space-y-4 p-4 md:p-6">
                {commonMistakes.map((item, index) => <div key={index} className="flex gap-3 md:gap-4 p-3 md:p-4 rounded-lg bg-background border border-destructive/20">
                    <div className="flex-shrink-0">{item.icon}</div>
                    <div className="flex-1">
                      <div className="font-semibold mb-1 text-destructive text-sm md:text-base">{item.mistake}</div>
                      <div className="text-xs md:text-sm text-muted-foreground">
                        <span className="font-semibold text-foreground">Rozwiązanie: </span>
                        {item.solution}
                      </div>
                    </div>
                  </div>)}
              </CardContent>
            </Card>
            
            {/* Quick Wins */}
            <Card className="gradient-card border-none shadow-xl">
              <CardHeader className="p-4 md:p-6">
                <CardTitle className="text-xl md:text-2xl flex items-center gap-2 md:gap-3">
                  <TrendingUp className="h-5 w-5 md:h-7 md:w-7 text-primary flex-shrink-0" />
                  <span>Quick Wins - Twoja checklist</span>
                </CardTitle>
                <CardDescription className="text-sm md:text-base">
                  Zacznij od tych działań – zobaczysz pierwsze efekty już za kilka tygodni
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 md:space-y-3 p-4 md:p-6">
                {quickWins.map(item => <div key={item.id} className="flex items-center space-x-3 md:space-x-4 p-3 md:p-4 rounded-lg hover:bg-secondary/50 transition-all border border-secondary">
                    <Checkbox id={item.id} checked={checkedItems[item.id] || false} onCheckedChange={checked => setCheckedItems(prev => ({
                  ...prev,
                  [item.id]: checked === true
                }))} className="h-4 w-4 md:h-5 md:w-5 flex-shrink-0" />
                    <label htmlFor={item.id} className="text-sm md:text-base font-medium leading-snug cursor-pointer flex-1">
                      {item.label}
                    </label>
                  </div>)}
              </CardContent>
            </Card>
            
            {/* Download Report CTA */}
            <Card className="gradient-cta-red border-none shadow-2xl text-white overflow-hidden relative">
              <div className="absolute inset-0 bg-gradient-to-r from-white/5 to-transparent" />
              <CardContent className="p-8 md:p-12 relative z-10">
                <div className="max-w-3xl mx-auto text-center space-y-8">
                  <div>
                    <h2 className="text-3xl md:text-4xl font-bold mb-4">Pobierz swój indywidualny raport, checklistę i zalecenia
                  </h2>
                    <p className="text-lg opacity-90">
                      Zostaw swój email, a prześlemy Ci szczegółowy raport PDF z Twoimi wynikami i rekomendacjami.
                    </p>
                  </div>

                  <DownloadReportEmailForm quizSubmissionId={(location.state as any)?.quizSubmissionId} score={score} band={band} />
                </div>
              </CardContent>
            </Card>
            
          </div>
        </div>
      </div>
      <ReportFooter />
      
      {/* Hidden Dialog Trigger for Lead Form */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogTrigger asChild>
          <button id="lead-form-trigger" className="hidden" />
        </DialogTrigger>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Porozmawiajmy o bezpiecznym starcie</DialogTitle>
          </DialogHeader>
          <LeadForm
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
          />
        </DialogContent>
      </Dialog>
    </ScrollArea>
  </>;
};
export default ReportLow;